import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useConfigApp } from '../use-config-app/use-config-app';
import { useConfigAppPath } from '../use-config-app-path/use-config-app-path';

/**
 *
 * @param defaultLocale - The default locale to fall back to
 * @returns An object with current locale and setter function
 */
export const useLocales = () => {
  const { defaultLocale, locales } = useConfigApp();
  const params = useParams<{
    localeId?: string;
    entityInstanceId?: string;
    tabId: string;
    moduleId: string;
    childModuleId?: string;
    environmentKey: string;
  }>();

  const navigate = useNavigate();
  const location = useLocation();

  const generateConfigPaths = useConfigAppPath();

  const { getEntityCreatePathForLocale, getEntityDetailsPathForLocale } =
    generateConfigPaths({
      tabId: params.tabId,
      moduleId: params.moduleId,
      childModuleId: params.childModuleId,
      environmentKey: params.environmentKey,
    });

  // Get current locale from URL path params or use default
  const currentLocale = params.localeId || defaultLocale;

  // Check if tenant has multiple locales available
  const hasMultipleLocales = React.useMemo(
    () => locales && locales.length > 1,
    [locales],
  );

  const setLocale = React.useCallback(
    (newLocale: string) => {
      const newPath = params.entityInstanceId
        ? getEntityDetailsPathForLocale(params.entityInstanceId, newLocale)
        : getEntityCreatePathForLocale(newLocale);

      navigate(
        {
          pathname: newPath,
          search: location.search,
        },
        {
          state: location.state,
        },
      );
    },
    [
      getEntityCreatePathForLocale,
      getEntityDetailsPathForLocale,
      location.search,
      location.state,
      navigate,
      params.entityInstanceId,
    ],
  );

  return {
    currentLocale,
    setLocale,
    defaultLocale,
    hasMultipleLocales,
    locales,
  };
};
